// Phase 3.5 (beads hh-3ky) — single-equipment loader.
//
// There is no GET /api/v1/equipment/{id} endpoint, so a single equipment node
// is loaded by walking GET /api/v1/properties/tree — the same approach useItem
// uses for items. The walk resolves the EquipmentNode plus a human-readable
// location label (property → room, or just property for property-level kit).

import { useEffect, useState } from 'preact/hooks';
import { properties, humanizeError } from '../../api';
import type {
  EquipmentNode,
  PropertyTreeNode,
  PropertyTreeResponse,
} from '../../api/endpoints/properties.js';

export type EquipmentStatus = 'loading' | 'ready' | 'not-found' | 'error';

/** An equipment node located within the property tree. */
export interface LocatedEquipment {
  readonly node: EquipmentNode;
  /** Human-readable location, e.g. "Дача → Котельная". */
  readonly locationLabel: string;
}

export interface EquipmentState {
  readonly status: EquipmentStatus;
  readonly equipment: LocatedEquipment | null;
  readonly error: string | null;
  reload(): void;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

/** Walk the tree, locate `equipmentId`, derive its location label. */
function locate(
  tree: readonly PropertyTreeNode[],
  equipmentId: string,
): LocatedEquipment | null {
  for (const property of tree) {
    for (const direct of property.equipment) {
      if (direct.id === equipmentId) {
        return { node: direct, locationLabel: property.name };
      }
    }
    for (const room of property.rooms) {
      for (const eq of room.equipment) {
        if (eq.id === equipmentId) {
          return {
            node: eq,
            locationLabel: `${property.name} → ${room.name}`,
          };
        }
      }
    }
  }
  return null;
}

export function useEquipment(equipmentId: string): EquipmentState {
  const [status, setStatus] = useState<EquipmentStatus>('loading');
  const [equipment, setEquipment] = useState<LocatedEquipment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    properties
      .getPropertyTree()
      .then((res: PropertyTreeResponse) => {
        if (cancelled) return;
        const found = locate(res.properties, equipmentId);
        if (found === null) {
          setEquipment(null);
          setStatus('not-found');
          return;
        }
        setEquipment(found);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [equipmentId, nonce]);

  return {
    status,
    equipment,
    error,
    reload: () => setNonce((n) => n + 1),
  };
}
